import { useEffect, useState, useCallback, useRef } from "react";
import { useDebouncedValue } from "../lib/useDebounce";
import { Search, RefreshCw, ScrollText, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ACTION_COLORS = {
  RFQ_CREATED: "bg-blue-100 text-blue-700",
  RFQ_STATUS_CHANGED: "bg-amber-100 text-amber-700",
  RFQ_DELETED: "bg-red-100 text-red-700",
  SUPPLIER_RFQ_SENT: "bg-purple-100 text-purple-700",
  SUPPLIER_QUOTE_RECORDED: "bg-cyan-100 text-cyan-700",
  SUPPLIER_QUOTE_SUBMITTED: "bg-cyan-100 text-cyan-700",
  SUPPLIER_RFQ_DECLINED: "bg-slate-200 text-slate-600",
  CUSTOMER_QUOTE_CREATED: "bg-indigo-100 text-indigo-700",
  CUSTOMER_QUOTE_STATUS_CHANGED: "bg-indigo-100 text-indigo-700",
  CUSTOMER_QUOTE_SENT: "bg-sky-100 text-sky-700",
  CUSTOMER_QUOTE_ACCEPTED: "bg-green-100 text-green-700",
  CUSTOMER_QUOTE_DECLINED: "bg-red-100 text-red-700",
  QUOTE_CHANGES_REQUESTED: "bg-fuchsia-100 text-fuchsia-700",
};

export function AuditBadge({ action }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-semibold whitespace-nowrap ${ACTION_COLORS[action] || "bg-slate-100 text-slate-600"}`}>
      {(action || "").replace(/_/g, " ")}
    </span>
  );
}

export default function AuditLog() {
  const [items, setItems] = useState(null);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q);
  const seq = useRef(0);

  const load = useCallback(() => {
    const s = ++seq.current;
    api.get("/admin/audit-log", { params: { action: action || undefined, q: dq || undefined } })
      .then((r) => { if (s === seq.current) { setItems(r.data.items); setActions(r.data.actions); } })
      .catch(() => { if (s === seq.current) setItems([]); });
  }, [action, dq]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/audit-log/export.csv", { responseType: "blob", params: { action: action || undefined, q: dq || undefined } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed"); }
  };

  return (
    <div data-testid="admin-audit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)] flex items-center gap-2.5"><ScrollText size={24} /> Audit Trail</h1>
        <div className="flex items-center gap-2">
          <button data-testid="audit-export-csv" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]">
            <FileDown size={13} /> Export CSV
          </button>
          <button data-testid="audit-refresh-button" onClick={load}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>
      <p className="mt-2 text-[13.5px] text-slate-500">Immutable record of RFQ, supplier and quote activity across the platform.</p>

      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="audit-search-input" className="pg-input !pl-9 !py-2.5 w-[260px]" placeholder="Search RFQ #, quote #, actor..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="audit-action-filter" className="pg-input !py-2.5 w-[280px]" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-left text-[13px] min-w-[860px]">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins]">
              <th className="px-5 py-3">Time (UTC)</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Reference</th>
              <th className="px-4 py-3">Actor</th><th className="px-5 py-3">Details</th>
            </tr>
          </thead>
          <tbody>
            {items === null && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400">Loading...</td></tr>}
            {items !== null && items.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-400" data-testid="audit-empty-state">No audit entries found.</td></tr>}
            {(items || []).map((e) => (
              <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`audit-row-${e.id}`}>
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3"><AuditBadge action={e.action} /></td>
                <td className="px-4 py-3 font-[Poppins] font-semibold text-[var(--pg-navy)] whitespace-nowrap">{e.entity_ref || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{e.actor}</td>
                <td className="px-5 py-3 text-slate-600 max-w-[380px]">{e.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
